import React, { useState, useEffect } from 'react';
import { 
  Building, Save, Plus, Trash2, Edit, X, CheckCircle, AlertCircle, 
  ArrowLeft, RefreshCw, Loader 
} from 'lucide-react';

interface BankDetail {
  id?: number;
  bank_name: string;
  branch?: string;
  account_number: string;
  account_name: string;
  currency: string;
  swift_code?: string;
  routing_number?: string;
  instructions?: string;
  is_active: boolean;
  is_default: boolean;
}

interface AdminBankDetailsProps {
  onNavigate: (page: string) => void;
}

const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? 'https://sparklinggems.shop/api'
  : 'http://localhost/sparkling-gems-backend-php/api';

// ✅ FIXED: Use the exact same key as in your update.php
const ADMIN_KEY = 'sparkling-gems-admin-2026';

const AdminBankDetails: React.FC<AdminBankDetailsProps> = ({ onNavigate }) => {
  const [banks, setBanks] = useState<BankDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingBank, setEditingBank] = useState<BankDetail | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [formData, setFormData] = useState<Partial<BankDetail>>({
    bank_name: '',
    branch: '',
    account_number: '',
    account_name: 'Sparkling Gems Inc.',
    currency: 'PHP',
    swift_code: '',
    routing_number: '',
    instructions: '',
    is_active: true,
    is_default: false
  });

  useEffect(() => {
    fetchBanks();
  }, []);

  const fetchBanks = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('Fetching banks from:', `${API_BASE_URL}/bank-details/update.php`);
      const response = await fetch(`${API_BASE_URL}/bank-details/update.php`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Banks response:', data);
      
      if (data.success) {
        setBanks(data.banks || []);
      } else {
        setError(data.error || 'Failed to fetch banks');
      }
    } catch (error) {
      console.error('Error fetching banks:', error);
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setSaving(true);

    try {
      const payload = editingBank ? { ...formData, id: editingBank.id } : formData;
      console.log('Saving bank:', payload);

      const response = await fetch(`${API_BASE_URL}/bank-details/update.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Key': ADMIN_KEY // ✅ Using the correct key
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      console.log('Save response:', data);
      
      if (data.success) {
        setSuccess(editingBank ? 'Bank details updated successfully' : 'Bank details added successfully');
        setShowForm(false);
        setEditingBank(null);
        resetForm();
        fetchBanks();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(data.error || 'Failed to save bank details');
      }
    } catch (error) {
      console.error('Save error:', error);
      setError('Connection error. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (bank: BankDetail) => {
    setEditingBank(bank);
    setFormData(bank);
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Are you sure you want to delete this bank account?')) return;

    try {
      const response = await fetch(`${API_BASE_URL}/bank-details/update.php?id=${id}`, {
        method: 'DELETE',
        headers: { 
          'X-Admin-Key': ADMIN_KEY // ✅ Using the correct key
        }
      });
      
      const data = await response.json();
      if (data.success) {
        setSuccess('Bank deleted successfully');
        fetchBanks();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(data.error || 'Failed to delete bank');
      }
    } catch (error) {
      console.error('Delete error:', error);
      setError('Connection error. Please try again.');
    }
  };

  const handleToggleActive = async (bank: BankDetail) => {
    try {
      const updatedBank = { ...bank, is_active: !bank.is_active };
      const response = await fetch(`${API_BASE_URL}/bank-details/update.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Key': ADMIN_KEY
        },
        body: JSON.stringify(updatedBank)
      });
      const data = await response.json();
      if (data.success) {
        fetchBanks();
      } else {
        setError(data.error || 'Failed to update bank');
      }
    } catch (error) {
      console.error('Toggle error:', error);
      setError('Connection error. Please try again.');
    }
  };

  const resetForm = () => {
    setFormData({
      bank_name: '',
      branch: '',
      account_number: '',
      account_name: 'Sparkling Gems Inc.',
      currency: 'PHP',
      swift_code: '',
      routing_number: '',
      instructions: '',
      is_active: true,
      is_default: false
    });
  };

  const handleAddNew = () => {
    setEditingBank(null);
    resetForm();
    setShowForm(true);
  };

  if (loading) {
    return (
      <div className="pt-44 pb-12 min-h-screen bg-stone-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex justify-center items-center h-64">
            <div className="text-center">
              <Loader size={32} className="animate-spin text-gold-600 mx-auto mb-4" />
              <p className="text-stone-500">Loading bank accounts...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-44 pb-12 min-h-screen bg-stone-50">
      <div className="max-w-7xl mx-auto px-6">
        
        {/* Header with back button */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <button 
              onClick={() => onNavigate('admin-dashboard')} 
              className="flex items-center gap-2 text-stone-500 hover:text-stone-900 text-xs uppercase tracking-widest mb-2 transition-colors"
            >
              <ArrowLeft size={14} /> Back to Dashboard
            </button>
            <h1 className="font-serif text-3xl text-stone-900">Bank Account Management</h1>
            <p className="text-stone-500 font-light mt-2 text-sm">Manage bank transfer details displayed at checkout</p>
          </div>
          
          <button
            onClick={handleAddNew}
            className="px-6 py-3 bg-gold-600 text-white rounded-sm hover:bg-gold-700 flex items-center gap-2 shadow-lg"
          >
            <Plus size={16} /> Add New Bank Account
          </button>
        </div>

        {/* Status Messages */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-sm flex items-center gap-2 text-red-700">
            <AlertCircle size={16} /> {error}
            <button onClick={() => setError(null)} className="ml-auto text-red-500 hover:text-red-700">
              <X size={16} />
            </button>
          </div>
        )}

        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-sm flex items-center gap-2 text-green-700">
            <CheckCircle size={16} /> {success}
            <button onClick={() => setSuccess(null)} className="ml-auto text-green-500 hover:text-green-700">
              <X size={16} />
            </button>
          </div>
        )}

        {/* Banks Table */}
        <div className="bg-white border border-stone-200 rounded-sm overflow-hidden shadow-lg">
          {banks.length === 0 ? (
            <div className="p-12 text-center text-stone-400">
              <Building size={48} className="mx-auto mb-4 text-stone-300" />
              <p className="text-lg mb-2">No bank accounts added yet</p>
              <p className="text-sm mb-4">Add your first bank account to display at checkout</p>
              <button 
                onClick={handleAddNew}
                className="text-gold-600 hover:text-gold-500 text-sm underline"
              >
                Add Bank Account
              </button>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-stone-100">
                <tr>
                  <th className="px-6 py-4 text-left text-xs uppercase text-stone-500 font-bold">Bank</th>
                  <th className="px-6 py-4 text-left text-xs uppercase text-stone-500 font-bold">Account Details</th>
                  <th className="px-6 py-4 text-left text-xs uppercase text-stone-500 font-bold">Currency</th>
                  <th className="px-6 py-4 text-left text-xs uppercase text-stone-500 font-bold">Status</th>
                  <th className="px-6 py-4 text-right text-xs uppercase text-stone-500 font-bold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {banks.map(bank => (
                  <tr key={bank.id} className="hover:bg-stone-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="font-medium text-stone-900">{bank.bank_name}</div>
                      {bank.branch && (
                        <div className="text-xs text-stone-500">{bank.branch}</div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-mono text-sm">{bank.account_number}</div>
                      <div className="text-xs text-stone-500">{bank.account_name}</div>
                      {(bank.swift_code || bank.routing_number) && (
                        <div className="text-[10px] text-stone-400 mt-1">
                          {bank.swift_code && `SWIFT: ${bank.swift_code}`}
                          {bank.swift_code && bank.routing_number && ' • '}
                          {bank.routing_number && `Routing: ${bank.routing_number}`}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-medium">{bank.currency}</span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-1">
                        {bank.is_default && (
                          <span className="inline-block px-2 py-0.5 text-[10px] font-bold bg-blue-100 text-blue-700 rounded-full w-fit">
                            Default
                          </span>
                        )}
                        <button
                          onClick={() => handleToggleActive(bank)}
                          className={`inline-block px-2 py-0.5 text-[10px] font-bold rounded-full w-fit cursor-pointer ${
                            bank.is_active 
                              ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                              : 'bg-stone-100 text-stone-500 hover:bg-stone-200'
                          }`}
                        >
                          {bank.is_active ? 'Active' : 'Inactive'}
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() => handleEdit(bank)}
                        className="text-gold-600 hover:text-gold-700 mr-3 p-1"
                        title="Edit"
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        onClick={() => bank.id && handleDelete(bank.id)}
                        className="text-red-600 hover:text-red-700 p-1"
                        title="Delete"
                        disabled={bank.is_default}
                      >
                        <Trash2 size={16} className={bank.is_default ? 'opacity-30' : ''} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Add/Edit Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white p-8 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="font-serif text-2xl">
                  {editingBank ? 'Edit' : 'Add'} Bank Account
                </h2>
                <button onClick={() => setShowForm(false)} className="text-stone-400 hover:text-stone-600">
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs uppercase text-stone-500 font-bold mb-1">
                      Bank Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="bank_name"
                      value={formData.bank_name}
                      onChange={handleInputChange}
                      required
                      className="w-full border border-stone-300 p-2 rounded-sm focus:border-gold-600 focus:outline-none"
                      placeholder="e.g., BPI - Bank of the Philippine Islands"
                    />
                  </div>
                  <div>
                    <label className="block text-xs uppercase text-stone-500 font-bold mb-1">
                      Branch (Optional)
                    </label>
                    <input
                      type="text"
                      name="branch"
                      value={formData.branch}
                      onChange={handleInputChange}
                      className="w-full border border-stone-300 p-2 rounded-sm focus:border-gold-600 focus:outline-none"
                      placeholder="e.g., Main Branch"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs uppercase text-stone-500 font-bold mb-1">
                    Account Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="account_number"
                    value={formData.account_number}
                    onChange={handleInputChange}
                    required
                    className="w-full border border-stone-300 p-2 rounded-sm focus:border-gold-600 focus:outline-none"
                    placeholder="e.g., 1234-5678-90"
                  />
                </div>

                <div>
                  <label className="block text-xs uppercase text-stone-500 font-bold mb-1">
                    Account Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="account_name"
                    value={formData.account_name}
                    onChange={handleInputChange}
                    required
                    className="w-full border border-stone-300 p-2 rounded-sm focus:border-gold-600 focus:outline-none"
                    placeholder="e.g., Sparkling Gems Inc."
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs uppercase text-stone-500 font-bold mb-1">
                      Currency
                    </label>
                    <select
                      name="currency"
                      value={formData.currency}
                      onChange={handleInputChange}
                      className="w-full border border-stone-300 p-2 rounded-sm focus:border-gold-600 focus:outline-none"
                    >
                      <option value="PHP">PHP - Philippine Peso</option>
                      <option value="USD">USD - US Dollar</option>
                      <option value="EUR">EUR - Euro</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs uppercase text-stone-500 font-bold mb-1">
                      SWIFT Code
                    </label>
                    <input
                      type="text"
                      name="swift_code"
                      value={formData.swift_code}
                      onChange={handleInputChange}
                      className="w-full border border-stone-300 p-2 rounded-sm focus:border-gold-600 focus:outline-none"
                      placeholder="e.g., BOPIPHMM"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs uppercase text-stone-500 font-bold mb-1">
                    Routing Number
                  </label>
                  <input
                    type="text"
                    name="routing_number"
                    value={formData.routing_number}
                    onChange={handleInputChange}
                    className="w-full border border-stone-300 p-2 rounded-sm focus:border-gold-600 focus:outline-none"
                    placeholder="e.g., 123456789"
                  />
                </div>

                <div>
                  <label className="block text-xs uppercase text-stone-500 font-bold mb-1">
                    Instructions (Shown to customers)
                  </label>
                  <textarea
                    name="instructions"
                    value={formData.instructions}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full border border-stone-300 p-2 rounded-sm focus:border-gold-600 focus:outline-none"
                    placeholder="e.g., Please use your order number as reference when making the transfer."
                  />
                </div>

                <div className="flex items-center gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      name="is_active"
                      checked={formData.is_active}
                      onChange={handleInputChange}
                      className="w-4 h-4 accent-gold-600"
                    />
                    <span className="text-sm">Active</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      name="is_default"
                      checked={formData.is_default}
                      onChange={handleInputChange}
                      className="w-4 h-4 accent-gold-600"
                    />
                    <span className="text-sm">Default Bank</span>
                  </label>
                </div>

                <div className="flex gap-4 pt-4 border-t border-stone-200">
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex-1 bg-gold-600 text-white py-3 rounded-sm hover:bg-gold-700 flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {saving ? (
                      <>
                        <Loader size={16} className="animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save size={16} />
                        {editingBank ? 'Update' : 'Save'} Bank Details
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    className="flex-1 border border-stone-300 py-3 rounded-sm hover:bg-stone-50"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminBankDetails;